import React, { Component, useState, useRef, useEffect } from 'react'
import i18n from 'i18next'
import {
    SortableContainer, SortableElement, arrayMove, SortableHandle,
} from 'react-sortable-hoc'
import * as R from 'ramda'

import Badge from '../../UniformComponents/Badge'
import Button from '../../UniformComponents/Button'
import SplitButton from '../../UniformComponents/SplitButton'
import Tooltip from '../../UniformComponents/Tooltip'
import Menu from '../../UniformComponents/Menu'
import Input from '../../components/form/Input'
import Select from '../../components/form/Select'
import TextArea from '../../components/form/TextArea'
import DragIcon from '../../assets/images/move-icon.svg'
import Checkbox from '../../components/form/Checkbox'
import Enum from '../Enum'
import BubbleLoader from '../../UniformComponents/BubbleLoader'
import HoverInfo from '../../UniformComponents/HoverInfo'

import { QuestionOptionLang } from '../../Models/Question'

import { unescapeHtmlTags } from '../../utils/helpers'

import '../question-builder.scss'

const { contextOidLevel, questionType } = Enum

const DragHandle = SortableHandle(() => (
    <div className="drag-handler">
        <img src={DragIcon} draggable="false" alt="drag icon" />
    </div>
))

const SortOption = SortableElement(({
    option,
    optInd,
    selectedLocale,
    defaultLocale,
    initialLocale,
    clientLanguages,
    updateOption,
    remOption,
    onNumericChange,
    getMenu,
    hasNumericCodes,
    hasLiveResponse,
    getIcons,
    questionTypeCode,
    customFormSubQuestionType,
    changeSubQuestionType,
    subQuestionMap,
}) => {
    const menuList = getMenu(optInd)
    const icons = getIcons(optInd)
    const showIcons = icons.length > 0
    const hideOption = option.isHidden
    const reportingValue = R.pathOr(undefined, ['reportingValue'], option)
    const disableOption = (hasLiveResponse && !option.allowEdit && !option.isNew)
    const subQuestionType = subQuestionMap.has(option.renderKey)
        ? subQuestionMap.get(option.renderKey).type : null
    const inputVal = unescapeHtmlTags(
        option.getTitle(selectedLocale, defaultLocale, initialLocale, clientLanguages),
    )

    const [menuActive, setMenuActive] = useState(false)

    const optionRef = useRef(null)

    const handleInputBlur = ({ value }) => {
        updateOption(optInd, value)
        option.justAdded = false
    }

    // eslint-disable-next-line arrow-body-style
    useEffect(() => {
        return () => { option.justAdded = false }
    }, [])

    return (
        <div className="option-editor" ref={optionRef}>
            <div className="option">
                <div className={`choice ${hideOption ? 'hide-opt' : ''}`} style={{}}>
                    <DragHandle />
                    <Input
                        key={`option-${inputVal}`}
                        type="text"
                        cssClass={`answer-option input ${((showIcons || hasNumericCodes) && !disableOption) ? 'fade-right' : ''}`}
                        placeholder={i18n.t('questionBuilder.optionEditor.option.placeholder')}
                        defaultValue={inputVal}
                        onBlur={handleInputBlur}
                        disabled={disableOption}
                        warningMsg={!option.justAdded ? option.fallbackTranslationMsg : undefined}
                    />
                    {(showIcons) && (
                        <div className={`icon-row ${disableOption ? 'disabled' : ''}`}>
                            {hideOption && (
                                <Badge
                                    type="error"
                                    cssClass="rounded hide-badge"
                                    label={i18n.t('questionBuilder.icon.tooltip.isHidden')}
                                />
                            )}
                            {icons.map((icon) => (
                                <Tooltip
                                    key={icon.id}
                                    text={icon.tooltip}
                                    direction="bottom"
                                    isNew
                                >
                                    <i className={`opt-icon ${icon.className}`} />
                                </Tooltip>
                            ))}
                        </div>
                    )}
                    {hasNumericCodes && (
                        <div className="input-num">
                            <Input
                                type="number"
                                cssClass="num-edit"
                                placeholder={i18n.t('questionBuilder.enterValue')}
                                defaultValue={reportingValue}
                                onBlur={({ value }) => {
                                    onNumericChange(optInd, value)
                                }}
                                disabled={disableOption}
                            />
                        </div>
                    )}
                </div>
                <React.Fragment>
                    {(questionTypeCode === questionType.Form) && (
                        <Select
                            cssClass="subque-select"
                            options={customFormSubQuestionType}
                            value={{
                                value: subQuestionType,
                                label: i18n.t(`questionType.${subQuestionType}`),
                            }}
                            onBlur={({ value }) => changeSubQuestionType(option.renderKey, value)}
                        />
                    )}
                    {menuList.length > 0 && (
                        <Menu
                            cssClass={`option-menu ${menuActive ? 'active' : ''}`}
                            list={menuList}
                            width="495px"
                            dir="left"
                            onClose={() => {
                                setMenuActive((prevState) => !(prevState))
                            }}
                            hasUniqueLast
                        >
                            <div
                                className="menu-icon"
                                onClick={() => {
                                    setMenuActive((prevState) => !(prevState))
                                }}
                            >
                                <i className="fa-regular fa-ellipsis-vertical" />
                            </div>
                        </Menu>
                    )}
                    {!hasLiveResponse && (
                        <Tooltip
                            text={i18n.t('questionBuilder.optionEditor.deleteOption')}
                            direction="bottom"
                            isNew
                        >
                            <i
                                className="del-icon fa-light fa-circle-xmark"
                                onClick={() => { remOption(optInd) }}
                            />
                        </Tooltip>
                    )}
                </React.Fragment>
            </div>
        </div>
    )
})

const SortContainer = SortableContainer(({
    items,
    selectedLocale,
    defaultLocale,
    initialLocale,
    clientLanguages,
    updateOption,
    remOption,
    getMenu,
    hasNumericCodes,
    hasLiveResponse,
    onNumericChange,
    getIcons,
    questionTypeCode,
    customFormSubQuestionType,
    changeSubQuestionType,
    subQuestionMap,
}) => (
    <div id="sortable-container" className="option-list">
        {
            items.map((option, index) => (
                <SortOption
                    key={`item-${option.renderKey}`}
                    index={index}
                    option={option}
                    optInd={index}
                    selectedLocale={selectedLocale}
                    defaultLocale={defaultLocale}
                    initialLocale={initialLocale}
                    clientLanguages={clientLanguages}
                    updateOption={updateOption}
                    remOption={remOption}
                    onNumericChange={onNumericChange}
                    getMenu={getMenu}
                    hasNumericCodes={hasNumericCodes}
                    hasLiveResponse={hasLiveResponse}
                    getIcons={getIcons}
                    questionTypeCode={questionTypeCode}
                    customFormSubQuestionType={customFormSubQuestionType}
                    changeSubQuestionType={changeSubQuestionType}
                    subQuestionMap={subQuestionMap}
                />
            ))
        }
    </div>
))

class OptionEditor extends Component {
    constructor(props) {
        super(props)
        const { hasLiveResponse } = this.props
        this.isFormQuestion = [questionType.NumberForm, questionType.Form]
            .includes(props.questionTypeCode)
        this.tabs = [
            {
                id: 0,
                label: i18n.t('q_ans_edit'),
                icon: 'fa-light fa-pen-to-square',
                show: true,
            },
            {
                id: 1,
                label: i18n.t('q_ans_qedit'),
                icon: 'fa-light fa-paste',
                show: !this.isFormQuestion,
                disabled: hasLiveResponse,
            },
        ]
        this.state = {
            curId: 0,
            sortByBtn: false,
            options: this.isFormQuestion ? props.optionSet : props.optionSet.options,
        }
    }

    componentDidUpdate(prevProps) {
        const { optionSet, questionTypeCode } = this.props
        if (prevProps.questionTypeCode !== questionTypeCode) {
            this.isFormQuestion = [questionType.NumberForm, questionType.Form]
                .includes(questionTypeCode)
        }
        if (prevProps.optionSet !== optionSet) {
            this.setState({
                options: this.isFormQuestion ? optionSet : optionSet.options,
            })
        }
    }

    createOptionMenu = (index) => {
        const {
            optionSet, hasLiveResponse, contextOid, optionMenuComps, subQuestionMap,
            isExploriClient, isExploriAdmin, questionTypeCode, question, store,
        } = this.props
        const { options } = this.state
        const subQuestionIsMandatory = subQuestionMap.has(options[index].renderKey)
            ? subQuestionMap.get(options[index].renderKey).isMandatory : false

        const metricOptions = store.metrics ? store.metrics.map((m) => (
            { ...m, label: R.pathOr('', ['label', store.locale], m) }
        )) : []

        const optionMenuList = (
            [
                {
                    compName: 'allowEdit',
                    label: i18n.t('questionBuilder.optionsMenu.allowEdit'),
                    value: options[index].allowEdit,
                    icon: 'fa-light fa-pen-to-square',
                    menuTooltip: contextOid === contextOidLevel.SURVEY_BUILDER
                        ? i18n.t('questionBuilder.optionsMenu.tooltip.allowEdit')
                        : i18n.t('questionLibrary.question.option.tooltip.allowEdit'),
                    menuShow: hasLiveResponse && !options[index].isHidden,
                    onClick: () => { this.handleOptionMenu(index, 'allowEdit') },
                    iconShow: false,
                },
                {
                    compName: 'headerRow',
                    label: i18n.t('q_headerrows_set'),
                    value: options[index].isLocked,
                    icon: 'fa-light fa-subtitles',
                    menuShow: question.hasHeaderGroup(),
                    menuType: 'menu',
                    list: [
                        <div>
                            <h5
                                style={{
                                    margin: '0px',
                                    borderBottom: '1px solid #F7F8FA',
                                    paddingBottom: '5px',
                                    marginBottom: '20px',
                                }}
                            >
                                {i18n.t('q_headerrow_select')}
                            </h5>
                            <Select
                                options={store.getHeaderOptions()}
                                label="Header Row"
                                placeholder="None"
                                blurOnClear
                                isClearable
                                defaultValue={options[index].headerGroupId}
                                onBlur={({ value }) => store.setHeaderRow(options[index], value)}
                            />
                        </div>,
                    ],
                    iconShow: false,
                },
                {
                    compName: 'isExclusive',
                    label: i18n.t('questionBuilder.optionsMenu.isExclusive'),
                    value: options[index].isExclusive,
                    icon: 'fa-light fa-hexagon-check',
                    menuTooltip: i18n.t('questionBuilder.optionsMenu.tooltip.isExclusive'),
                    menuShow: true,
                    menuType: 'checkbox',
                    onClick: () => { this.handleOptionMenu(index, 'isExclusive') },
                    iconTooltip: i18n.t('questionBuilder.icon.tooltip.isExclusive'),
                    iconShow: true,
                },
                {
                    compName: 'isNonReporting',
                    label: i18n.t('questionBuilder.optionsMenu.isNonReporting'),
                    value: options[index].isNonReporting,
                    name: 'isNonReporting',
                    icon: 'fa-light fa-do-not-enter',
                    menuTooltip: i18n.t('questionBuilder.optionsMenu.tooltip.isNonReporting'),
                    menuShow: true,
                    menuType: 'checkbox',
                    onClick: () => { this.handleOptionMenu(index, 'isNonReporting') },
                    iconTooltip: i18n.t('questionBuilder.icon.tooltip.isNonReporting'),
                    iconShow: true,
                },
                {
                    compName: 'hasOptionOther',
                    label: i18n.t('questionBuilder.optionsMenu.hasOptionOther'),
                    value: options[index].hasOptionOther,
                    icon: 'fa-light fa-square-info',
                    menuTooltip: i18n.t('questionBuilder.optionsMenu.tooltip.hasOptionOther'),
                    menuShow: true,
                    menuType: 'checkbox',
                    onClick: () => { this.handleOptionMenu(index, 'hasOptionOther') },
                    iconTooltip: i18n.t('questionBuilder.icon.tooltip.hasOptionOther'),
                    iconShow: true,
                },
                {
                    compName: 'otherPiping',
                    label: i18n.t('questionBuilder.optionsMenu.otherPiping'),
                    value: options[index].hasOptionOther,
                    icon: 'fa-light fa-square-info',
                    menuTooltip: i18n.t('questionBuilder.optionsMenu.tooltip.otherPiping'),
                    menuShow: options[index].hasOptionOther,
                    menuType: 'menu',
                    list: [
                        <div>
                            <h5
                                style={{
                                    margin: '0px',
                                    borderBottom: '1px solid #F7F8FA',
                                    paddingBottom: '5px',
                                    marginBottom: '20px',
                                }}
                                dangerouslySetInnerHTML={{ __html: i18n.t('questionBuilder.optionsMenu.otherPiping') }}
                            />
                            <Select
                                options={store.getPipingBehaviours()}
                                label="Set Behaviour"
                                placeholder="None"
                                blurOnClear
                                isClearable
                                defaultValue={options[index].otherPiping}
                                onBlur={({ value }) => store.setPipingBehaviour(options[index], value)}
                            />
                        </div>,
                    ],
                    iconShow: false,
                },
                {
                    compName: 'metrics',
                    label: 'Metrics',
                    value: true,
                    icon: 'fa-light fa-square-info',
                    menuShow: R.pathOr(false, ['question', 'optionSet', 'hasMetrics'], store) && store.appStore.isExploriAdmin(),
                    menuType: 'menu',
                    list: [
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                            <Select
                                options={metricOptions}
                                label="Metrics"
                                placeholder="None"
                                blurOnClear
                                isClearable
                                isMulti
                                defaultValue={options[index].metricIds}
                                onBlur={({ value }) => store.updateOptionMetrics(index, value.map((v) => v.value), 'metricIds')}
                            />
                            <Select
                                options={metricOptions
                                    .filter((m) => R.pathOr([], [index, 'metricIds'], options).includes(m.value))}
                                label="Default metric"
                                placeholder="None"
                                blurOnClear
                                isClearable
                                defaultValue={options[index].defaultMetric}
                                onBlur={({ value }) => store.updateOptionMetrics(index, value, 'defaultMetric')}
                            />
                        </div>,
                    ],
                    iconShow: false,
                },
                {
                    compName: 'optionOtherIsMandatory',
                    label: i18n.t('questionBuilder.optionsMenu.optionOtherIsMandatory'),
                    value: options[index].optionOtherIsMandatory,
                    icon: 'fa-light fa-circle-exclamation',
                    menuTooltipTitle: i18n.t('questionBuilder.optionsMenu.tooltipTitle.optionOtherIsMandatory'),
                    menuTooltip: i18n.t('questionBuilder.optionsMenu.tooltip.optionOtherIsMandatory'),
                    menuShow: options[index].hasOptionOther,
                    menuType: 'checkbox',
                    onClick: () => { this.handleOptionMenu(index, 'optionOtherIsMandatory') },
                    iconTooltip: i18n.t('questionBuilder.icon.tooltip.optionOtherIsMandatory'),
                    iconShow: true,
                },
                {
                    compName: 'skipLogic',
                    label: i18n.t('questionBuilder.optionsMenu.addSkipLogic'),
                    value: options[index].validSkipLogic !== undefined,
                    icon: 'fa-light fa-arrows-repeat',
                    menuTooltip: i18n.t('questionBuilder.optionsMenu.tooltip.addSkipLogic'),
                    menuShow: contextOid === contextOidLevel.SURVEY_BUILDER,
                    onClick: () => { this.handleOptionMenu(index, 'skipLogic') },
                    iconTooltip: options[index].validSkipLogic
                        ? i18n.t('questionBuilder.icon.tooltip.validSkip')
                        : i18n.t('questionBuilder.icon.tooltip.invalidSkip'),
                    iconClass: options[index].validSkipLogic ? 'green' : 'red',
                    iconShow: true,
                },
                {
                    compName: 'isLocked',
                    label: i18n.t('questionBuilder.optionsMenu.isLocked'),
                    value: options[index].isLocked,
                    icon: 'fa-light fa-lock',
                    menuTooltip: i18n.t('questionBuilder.optionsMenu.tooltip.isLocked'),
                    menuShow: R.pathOr(false, ['isAlphaSort'], optionSet) || R.pathOr(false, ['isRandomized'], optionSet),
                    menuType: 'checkbox',
                    onClick: () => { this.handleOptionMenu(index, 'isLocked') },
                    iconTooltip: i18n.t('questionBuilder.icon.tooltip.isLocked'),
                    iconShow: true,
                },
                {
                    compName: 'editSubQue',
                    label: i18n.t('questionBuilder.optionsMenu.editQue'),
                    icon: 'fa-light fa-pen-to-square',
                    menuTooltip: i18n.t('questionBuilder.optionsMenu.tooltip.editQue'),
                    menuShow: true,
                    onClick: () => { this.handleOptionMenu(index, 'editSubQue') },
                    iconShow: false,
                },
                {
                    compName: 'subQueMandatory',
                    label: i18n.t('questionBuilder.optionsMenu.subQueMandatory'),
                    value: subQuestionIsMandatory,
                    icon: 'fa-light fa-circle-exclamation',
                    menuTooltipTitle: i18n.t('questionBuilder.optionsMenu.tooltipTitle.subQueMandatory'),
                    menuTooltip: i18n.t('questionBuilder.optionsMenu.tooltip.subQueMandatory'),
                    menuShow: true,
                    menuType: 'checkbox',
                    onClick: () => { this.handleOptionMenu(index, 'subQueIsMandatory') },
                    iconTooltip: i18n.t('questionBuilder.icon.tooltip.subQueMandatory'),
                    iconShow: true,
                },
                {
                    compName: 'fieldType',
                    label: i18n.t('questionBuilder.optionsMenu.isNonExclusive'),
                    value: options[index].fieldType === questionType.MultipleChoice,
                    icon: 'fa-light fa-list',
                    menuTooltip: i18n.t('questionBuilder.optionsMenu.tooltip.isNonExclusive'),
                    menuShow: true,
                    menuType: 'checkbox',
                    onClick: () => { this.handleOptionMenu(index, 'fieldType') },
                    iconTooltip: i18n.t('questionBuilder.icon.tooltip.fieldType'),
                    iconShow: true,
                },
                {
                    compName: 'optionExclusive',
                    label: i18n.t('questionBuilder.optionsMenu.optionExclusive'),
                    value: options[index].isExclusive,
                    icon: 'fa-light fa-hexagon-check',
                    menuTooltip: i18n.t('questionBuilder.optionsMenu.tooltipTitle.optionExclusive'),
                    menuShow: questionTypeCode === questionType.Grid
                    && options[index].fieldType === questionType.MultipleChoice
                    && isExploriClient()
                    && isExploriAdmin(),
                    menuType: 'checkbox',
                    onClick: () => { this.handleOptionMenu(index, 'optionExclusive') },
                    iconTooltip: i18n.t('questionBuilder.icon.tooltip.optionExclusive'),
                    iconShow: true,
                },
                {
                    compName: 'optionNumeric',
                    label: i18n.t('questionBuilder.optionsMenu.optionNumeric'),
                    value: options[index].fieldType === questionType.NumberRange,
                    icon: 'fa-sharp fa-regular fa-rectangle',
                    menuTooltip: i18n.t('questionBuilder.optionsMenu.tooltipTitle.optionNumeric'),
                    menuShow: questionTypeCode === questionType.Grid
                    && isExploriClient()
                    && isExploriAdmin(),
                    menuType: 'checkbox',
                    onClick: () => { this.handleOptionMenu(index, 'optionNumeric') },
                    iconTooltip: i18n.t('questionBuilder.icon.tooltip.optionNumeric'),
                    iconShow: true,
                },
                {
                    compName: 'delete',
                    label: i18n.t('delete'),
                    icon: 'fa-light fa-circle-xmark',
                    greyBg: true,
                    menuShow: !hasLiveResponse,
                    onClick: () => { this.handleOptionMenu(index, 'delete') },
                },
                {
                    compName: 'isHidden',
                    label: options[index].isHidden
                        ? i18n.t('questionBuilder.optionsMenu.notIsHidden')
                        : i18n.t('questionBuilder.optionsMenu.isHidden'),
                    value: options[index].isHidden,
                    icon: options[index].isHidden
                        ? 'fa-light fa-eye'
                        : 'fa-light fa-eye-slash',
                    greyBg: true,
                    menuShow: hasLiveResponse || options[index].isHidden,
                    onClick: () => { this.handleOptionMenu(index, 'isHidden') },
                    iconTooltip: i18n.t('questionBuilder.icon.tooltip.isHidden'),
                    iconShow: true,
                },
            ]
        )
        return optionMenuList
            .filter((optionMenu) => (optionMenuComps.includes(optionMenu.compName)))
    }

    handleOptionMenu = async (index, type) => {
        const {
            selectedLocale, defaultLocale, toggleOptionEditModal, questionTypeCode,
            initialLocale, name, toggleSubQueModal, toggleSubQueMandatory, store,
        } = this.props
        const { options } = this.state

        switch (type) {
            case 'isExclusive':
            case 'isNonReporting':
            case 'optionOtherIsMandatory':
            case 'isLocked':
                options[index][type] = !options[index][type]
                this.onOptionChange(options.slice())
                break
            case 'fieldType':
                options[index].fieldType = (
                    options[index].fieldType !== questionType.MultipleChoice
                        ? questionType.MultipleChoice : questionType.SingleChoice
                )
                if (questionTypeCode === questionType.Grid
                    && options[index].fieldType !== questionType.MultipleChoice) {
                    options[index].isExclusive = false
                }
                this.onOptionChange(options.slice())
                break
            case 'delete':
                this.remOption(index)
                break
            case 'skipLogic': {
                if (store.isNewOption(options[index])) {
                    store.toggleSkipLogicAlertModal()
                    break
                }
                store.appStore.skipLogicConfig = {
                    ...store.appStore.skipLogicConfig,
                    type: 'option',
                    title: options[index].getTitle(selectedLocale, defaultLocale, initialLocale),
                    option: options[index],
                    optionSetName: name,
                }
                store.appStore.skipLogicModalVisible = true
                break
            }
            case 'editSubQue':
                toggleSubQueModal(options[index].renderKey)
                break
            case 'subQueIsMandatory':
                toggleSubQueMandatory(options[index].renderKey)
                break
            case 'isHidden':
                options[index][type] = !options[index][type]
                if (options[index][type]) options[index].allowEdit = false
                this.onOptionChange(options.slice())
                break
            case 'hasOptionOther':
                options[index][type] = !options[index][type]
                if (!options[index][type]) options[index].optionOtherIsMandatory = false
                this.onOptionChange(options.slice())
                break
            case 'allowEdit':
                toggleOptionEditModal(index, name)
                break
            case 'optionNumeric':
                options[index].fieldType = (
                    options[index].fieldType !== questionType.NumberRange
                        ? questionType.NumberRange : questionType.SingleChoice
                )
                if (questionTypeCode === questionType.Grid
                    && options[index].fieldType !== questionType.MultipleChoice) {
                    options[index].isExclusive = false
                }
                this.onOptionChange(options.slice())
                break
            case 'optionExclusive':
                options[index].isExclusive = !options[index].isExclusive
                this.onOptionChange(options.slice())
                break
            default:
                break
        }
    }

    onOptionChange = (newOptions) => {
        const { optionSet, onBlur } = this.props
        if (this.isFormQuestion) {
            onBlur({ value: newOptions })
        } else {
            optionSet.options = newOptions
            onBlur({ value: optionSet })
        }
    }

    addAnswerOption = (text) => {
        const {
            availableLocales, initialLocale, selectedLocale, optionSet,
        } = this.props
        let option = {
            allowEdit: true,
            langs: {},
        }

        switch (text) {
            case 'Other': {
                const otherLangs = {
                    en_GB: 'Other',
                    de_DE: 'Andere',
                    fr_FR: 'Autre',
                    it_IT: 'Altro',
                    nl_NL: 'Anders',
                    pt_BR: 'Outro',
                    ru_RU: '\u0414\u0440\u0443\u0433\u043E\u0435',
                    tr_TR: 'Di\u011Fer',
                    zh_CN: '\u5176\u4ED6',
                    zh_TW: '\u5176\u4ED6',
                }
                option = {
                    ...option,
                    isLocked: optionSet.isAlphaSort || optionSet.isRandomized,
                    hasOptionOther: true,
                }
                availableLocales.forEach(({ value }) => {
                    const langText = Object.hasOwnProperty.call(otherLangs, value)
                        ? otherLangs[value] : otherLangs.en_GB
                    option.langs[value] = { text: langText }
                })
                break
            }
            case 'None of these': {
                const noneLangs = {
                    en_GB: 'None of these',
                    de_DE: 'Keine der genannten',
                    fr_FR: 'Aucune de ces propositions',
                    it_IT: 'Nessuno di questi',
                    nl_NL: 'Geen van bovenstaande',
                    pt_BR: 'Nenhuma destas',
                    ru_RU: '\u041D\u0438\u0447\u0435\u0433\u043E \u0438\u0437 \u043F\u0435\u0440\u0435\u0447\u0438\u0441\u043B\u0435\u043D\u043D\u043E\u0433\u043E',
                    tr_TR: 'Bunlar\u0131n hi\u00E7biri',
                    zh_CN: '\u4EE5\u4E0A\u90FD\u4E0D\u662F',
                    zh_TW: '\u4EE5\u4E0A\u7686\u975E',
                }
                option = {
                    ...option,
                    isLocked: optionSet.isAlphaSort || optionSet.isRandomized,
                    isExclusive: true,
                    isNonReporting: true,
                }
                availableLocales.forEach(({ value }) => {
                    const langText = Object.hasOwnProperty.call(noneLangs, value)
                        ? noneLangs[value] : noneLangs.en_GB
                    option.langs[value] = { text: langText }
                })
                break
            }
            default: {
                option.langs[initialLocale || selectedLocale] = { text }
                break
            }
        }
        return option
    }

    remOption = (index) => {
        const { remSubQuestion } = this.props
        const { options } = this.state
        const filteredOptions = options.filter((o, i) => (index !== i))
        this.onOptionChange(filteredOptions.slice())
        // remove subquestion
        if (this.isFormQuestion) {
            remSubQuestion(options[index].renderKey)
        }
    }

    updateOption = (index, text) => {
        const {
            initialLocale, selectedLocale, defaultLocale,
        } = this.props
        const { options } = this.state
        const locale = selectedLocale || defaultLocale || initialLocale

        if (this.isFormQuestion) {
            const shortTitleMap = options[index].question.content.shortTitle
            if (shortTitleMap.has(locale)) {
                options[index].question.content.shortTitle.get(locale).text = text
            } else {
                options[index].question.content.shortTitle.set(
                    locale,
                    new QuestionOptionLang({ id: undefined, text }),
                )
            }
        } else {
            const optionsMap = options[index].langs
            if (optionsMap.has(locale)) {
                options[index].langs.get(locale).text = text
            } else {
                options[index].langs.set(locale, new QuestionOptionLang({ id: undefined, text }))
            }
        }
        this.setState({ options }, () => this.onOptionChange(options.slice()))
    }

    getOptionTexts = (options) => {
        const { initialLocale, selectedLocale, defaultLocale, clientLanguages } = this.props
        const texts = options.reduce((result, option) => {
            const text = unescapeHtmlTags(
                option.getTitle(selectedLocale, defaultLocale, initialLocale, clientLanguages),
            )
            return `${result + text}\n`
        }, '')
        return texts.substring(0, texts.length - 1)
    }

    onQuickEdit = ({ value }) => {
        const { optionSet: { options }, selectedLocale, addOption } = this.props
        if (value.length === 0) return

        const optionList = value.split('\n')
        // clear tailing empty option
        if (optionList[optionList.length - 1] === '') {
            optionList.pop()
        }

        optionList.forEach((optionText, index) => {
            if (options.length === 1 && optionText === '') return
            if (index < options.length) {
                if (!options[index].langs.has(selectedLocale)) {
                    options[index].langs.set(selectedLocale, {
                        text: optionText,
                    })
                }
                options[index].langs.get(selectedLocale).text = optionText
            } else {
                const newOption = this.addAnswerOption(optionText)
                addOption(newOption)
            }
        })
        options.splice(optionList.length)
        this.onOptionChange(options)
    }

    onSortEnd = ({ oldIndex, newIndex }) => {
        const { options } = this.state
        let sortedOptions = arrayMove(options.slice(), oldIndex, newIndex)
        sortedOptions = sortedOptions.map((option, index) => {
            option.priority = index + 1
            return option
        })
        this.onOptionChange(sortedOptions.slice())
    }

    onNumericChange = (index, value) => {
        const { options } = this.state
        options[index].reportingValue = (
            !R.isNil(value) && !R.isEmpty(value) && !isNaN(value)
        ) ? Number(value) : undefined
        this.onOptionChange(options.slice())
    }

    getOptionMenu = (optInd) => {
        const optionMenu = this.createOptionMenu(optInd).filter((config) => (config.menuShow))
        return (
            optionMenu.map((config) => {
                switch (config.menuType) {
                    case 'checkbox':
                        return (
                            <div className="option-line">
                                <i className={config.icon} />
                                <Checkbox
                                    name={config.label}
                                    label={config.label}
                                    labelDir="left"
                                    cssClass="option-check"
                                    tooltipText={config.menuTooltip}
                                    tooltipDir="right"
                                    checked={config.value}
                                    onBlur={config.onClick}
                                />
                            </div>
                        )
                    case 'menu':
                        return (
                            <Menu
                                cssClass="option-menu sub-menu"
                                list={config.list}
                                width="435px"
                                dir="left"
                                showTick
                            >
                                <div className="option-line">
                                    <i className={config.icon} />
                                    <p dangerouslySetInnerHTML={{ __html: i18n.t(config.label) }} />
                                    {config.menuTooltip
                                        && (
                                            <HoverInfo
                                                cssClass="hover-info"
                                                desc={config.menuTooltip}
                                                dir="right"
                                            >
                                                <i className="info-icon fa-solid fa-circle-question" />
                                            </HoverInfo>
                                        )}
                                </div>
                            </Menu>
                        )
                    default:
                        return (
                            <div className="question-item">
                                <div className="option-line" onClick={config.onClick}>
                                    <i className={config.icon} />
                                    <p dangerouslySetInnerHTML={{ __html: i18n.t(config.label) }} />
                                    {config.menuTooltip
                                        && (
                                            <HoverInfo
                                                cssClass="hover-info"
                                                desc={config.menuTooltip}
                                                dir="right"
                                            >
                                                <i className="info-icon fa-solid fa-circle-question" />
                                            </HoverInfo>
                                        )}
                                </div>
                            </div>
                        )
                }
            })
        )
    }

    getSortByMenu = () => {
        const { optionSet, changeSorting, sortByComps } = this.props
        const onSortChange = ({ name, value }) => {
            changeSorting(value, name)
            if (value) return
            optionSet.options.forEach((option, index) => {
                optionSet.options[index].isLocked = false
            })
        }
        const sortByList = [
            {
                compName: 'isRandomized',
                name: 'randomizedOptions',
                label: i18n.t('q_ans_random'),
                checked: R.pathOr(false, ['isRandomized'], optionSet),
                tooltipText: i18n.t('questionBuilder.sortMenu.tooltip.random'),
            },
            {
                compName: 'isAlphaSort',
                name: 'alphasort',
                label: i18n.t('q_ans_alpha'),
                checked: R.pathOr(false, ['isAlphaSort'], optionSet),
                tooltipText: i18n.t('questionBuilder.sortMenu.tooltip.alpha'),
            },
        ]
        return sortByList
            .filter((sortByComp) => (sortByComps.includes(sortByComp.compName)))
            .map((sortByComp) => (
                <Checkbox
                    name={sortByComp.name}
                    label={sortByComp.label}
                    checked={sortByComp.checked}
                    tooltipText={sortByComp.tooltipText}
                    labelDir="left"
                    cssClass="sort-check"
                    tooltipDir="left"
                    onBlur={onSortChange}
                />
            ))
    }

    getIcons = (optInd) => {
        const optionMenu = this.createOptionMenu(optInd)
        const icons = optionMenu.reduce((iconList, config, ind) => {
            if (config.iconShow && config.value) {
                iconList.push({ id: ind, className: `${config.icon} ${config.iconClass || ''}`, tooltip: config.iconTooltip })
            }
            return iconList
        }, [])
        return icons
    }

    renderHeader = () => {
        const { curId, sortByBtn } = this.state
        const sortByMenu = this.getSortByMenu()
        return (
            <div className="header form-tabs-header">
                <div className="tab-btns">
                    {this.tabs.filter((tab) => (tab.show)).map((tab) => (
                        <div className={`tab ${tab.id === curId ? 'selected' : ''}`}>
                            <Button
                                cssClass="tab-btn"
                                label={tab.label}
                                style={{ cursor: tab.disabled ? 'not-allowed' : 'pointer' }}
                                icon={tab.icon}
                                onClick={() => { if (!tab.disabled) this.setState({ curId: tab.id }) }}
                            />
                        </div>
                    ))}
                </div>
                {!this.isFormQuestion && sortByMenu.length > 0 && (
                    <Menu
                        cssClass="sort-menu"
                        list={sortByMenu}
                        width="200px"
                        dir="bottom"
                        closeAfterSelection
                        onClose={() => {
                            this.setState({ sortByBtn: false })
                        }}
                    >
                        <Button
                            cssClass="primary regular sort-btn"
                            label={i18n.t('questionBuilder.optionEditor.sortby', 'Sort by')}
                            onClick={() => {
                                this.setState(
                                    (prevState) => ({ sortByBtn: !prevState.sortByBtn }),
                                )
                            }}
                        >
                            <i className={sortByBtn ? 'fa-light fa-chevron-up' : 'fa-light fa-chevron-down'} />
                        </Button>
                    </Menu>
                )}
            </div>
        )
    }

    renderInheritedOptions = () => {
        const {
            inheritedOptions, selectedLocale, defaultLocale, initialLocale, clientLanguages,
        } = this.props
        if (!inheritedOptions) return null
        const optionTitles = inheritedOptions.reduce((acc, option) => {
            // run getTitle before render so language fallback warnings are set
            acc[option.id] = option.getTitle(selectedLocale, defaultLocale, initialLocale, clientLanguages);
            return acc
        }, {})

        return (
            <React.Fragment>
                {
                    inheritedOptions.map((option) => (
                        <div className="inherit-choice">
                            <div className="text-container">
                                {option.fallbackTranslationMsg && (
                                    <HoverInfo
                                        cssClass="warning-icon"
                                        desc={option.fallbackTranslationMsg}
                                        dir="right"
                                    >
                                        <i className="fa-regular fa-language warning" />
                                    </HoverInfo>
                                )}
                                {unescapeHtmlTags(optionTitles[option.id])}
                            </div>
                            <div className="icon-row disabled">
                                <Tooltip
                                    text={i18n.t('questionbuilder.icon.tooltip.inherited', 'Inherited option')}
                                    direction="bottom"
                                    isNew
                                >
                                    <i className="opt-icon fa-light fa-diagram-project" />
                                </Tooltip>
                            </div>
                        </div>
                    ))
                }
            </React.Fragment>
        )
    }

    renderOptions = () => {
        const {
            optionSet,
            hasLiveResponse,
            selectedLocale,
            defaultLocale,
            initialLocale,
            availableLocales,
            clientLanguages,
            questionTypeCode,
            customFormSubQuestionType,
            changeSubQuestionType,
            subQuestionMap,
        } = this.props
        const { options } = this.state
        const hasNumericCodes = R.pathOr(false, ['hasNumericCodes'], optionSet)
        return (
            <div key="sort-container">
                <SortContainer
                    items={options}
                    selectedLocale={selectedLocale}
                    defaultLocale={defaultLocale}
                    initialLocale={initialLocale}
                    availableLocales={availableLocales}
                    clientLanguages={clientLanguages}
                    updateOption={this.updateOption}
                    remOption={this.remOption}
                    onNumericChange={this.onNumericChange}
                    onSortEnd={this.onSortEnd}
                    helperClass="option-drag"
                    lockAxis="y"
                    useDragHandle
                    lockToContainerEdges
                    getMenu={this.getOptionMenu}
                    hasNumericCodes={hasNumericCodes}
                    hasLiveResponse={hasLiveResponse}
                    getIcons={this.getIcons}
                    questionTypeCode={questionTypeCode}
                    customFormSubQuestionType={customFormSubQuestionType}
                    changeSubQuestionType={changeSubQuestionType}
                    subQuestionMap={subQuestionMap}
                />
            </div>
        )
    }

    renderButtons = () => {
        const { buttonComps, addOption, name } = this.props
        const btnList = [
            {
                compName: 'other',
                icon: 'fa-light fa-circle-plus',
                btnLabel: i18n.t('q_ans_addother'),
                label: i18n.t('q_ans_other'),
                onClick: () => {
                    const newOption = this.addAnswerOption('Other')
                    addOption(newOption, name)
                },
            },
            {
                compName: 'none',
                icon: 'fa-light fa-circle-plus',
                btnLabel: i18n.t('q_ans_addnone'),
                label: i18n.t('q_ans_none'),
                onClick: () => {
                    const newOption = this.addAnswerOption('None of these')
                    addOption(newOption, name)
                },
            },
        ].filter((btn) => (buttonComps.includes(btn.compName)))

        const renderSplitButton = () => {
            if (btnList.length === 0) return null
            if (btnList.length === 1) {
                return (
                    <Button
                        cssClass="tertiary regular center sort-btn"
                        label={btnList[0].btnLabel}
                        icon={btnList[0].icon}
                        onClick={btnList[0].onClick}
                    />
                )
            }
            return (
                <SplitButton
                    label={i18n.t('q_ans_other_none', 'Add other or none')}
                    btnClass="tertiary regular add-other-btn"
                    buttons={btnList}
                />
            )
        }

        return (
            <div className="btn-wrapper">
                <Button
                    cssClass="primary regular add-answer-btn center"
                    label={i18n.t('q_ans_add')}
                    icon="fa-light fa-circle-plus"
                    onClick={() => {
                        const newOption = this.addAnswerOption('')
                        addOption(newOption, name)
                    }}
                />
                {renderSplitButton()}
            </div>
        )
    }

    renderTabs = () => {
        const { optionEditorLoader } = this.props
        const { curId } = this.state

        // show loader (now only used for prepopulate loading)
        if (optionEditorLoader) {
            return (
                <div className="content">
                    <BubbleLoader cssClass="loader" />
                </div>
            )
        }

        if (curId === 0) {
            return (
                <div className="content">
                    {!this.isFormQuestion && this.renderInheritedOptions()}
                    {this.renderOptions()}
                    {this.renderButtons()}
                </div>
            )
        }

        if (!this.isFormQuestion) {
            const { optionSet } = this.props
            const defaultVal = this.getOptionTexts(optionSet.options)
            return (
                <div className="content">
                    <TextArea
                        cssClass="bulk-textarea"
                        placeholder={i18n.t('questionBuilder.optionEditor.addBulk.placeholder')}
                        defaultValue={defaultVal}
                        onBlur={({ value }) => { this.onQuickEdit({ value }) }}
                        resize="vertical"
                    />
                </div>
            )
        }

        return null
    }

    render() {
        return (
            <div id="option-editor-container">
                {this.renderHeader()}
                {this.renderTabs()}
            </div>
        )
    }
}

export default OptionEditor
